
























































































































import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { get } from 'vuex-pathify';

import mixins from '@/utils/mixins';
import SnackbarMixin from '@/mixins/snackbar';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';

import { IQuote, Quote } from '@/models/quote';
// Utilities
import { timeAgo } from '@/utils/timeAgo';
import { CurrencyRate } from '@/models/currency';

import { ErrorManager } from '@/models/error';
import { IQuoteFetchActionPayload } from '@/store/quote';

import QuotesRevisionHistoryDialog from './RevisionHistoryDialog.vue';
import QuoteActivityDialog from './QuoteActivityDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import QuoteCategoryListItem from './category/QuoteCategoryListItem.vue';

export default mixins(SnackbarMixin, Functions, auth).extend({
  name: 'QuotesList',

  components: {
    QuotesRevisionHistoryDialog,
    QuoteActivityDialog,
    ErrorAlert,
    QuoteCategoryListItem
  },

  props: {
    search: String
  },

  data() {
    return {
      sortBy: 'number',
      page: Number(this.$route.query.page ?? 1),
      sortDesc: true,
      loading: false,
      headers: [
        {
          text: '#',
          align: 'start',
          width: 90,
          sortable: false,
          value: 'number'
        },
        {
          text: 'Revision',
          align: 'center',
          sortable: false,
          value: 'revision',
          width: 100
        },
        {
          text: 'Owner',
          align: 'owner',
          sortable: false,
          value: 'owner',
          width: 170
        },
        {
          text: 'Layout',
          sortable: false,
          value: 'layout',
          width: 260
        },
        {
          text: 'Project',
          sortable: false,
          value: 'project',
          width: 250
        },
        {
          text: 'Customer',
          sortable: false,
          value: 'customer',
          width: 250
        },
        {
          text: 'Amount',
          sortable: false,
          align: 'end',
          value: 'amount',
          width: 150
        },
        {
          text: 'Last Modified',
          align: 'center',
          justify: 'center',
          sortable: false,
          value: 'updated_at',
          width: 180
        },
        {
          text: 'Status',
          align: 'center',
          sortable: false,
          value: 'state',
          width: 150
        },
        {
          text: '',
          align: 'center',
          value: 'actions',
          width: 100
        }
      ],

      errorMessage: ''
    };
  },

  computed: {
    ...mapGetters({
      quotes: 'quote/getQuotes',
      numQuotes: 'quote/getQuoteCount'
    }),

    getCurrencyById: get<(id: number) => CurrencyRate>(
      'currency/getCurrencyById'
    )
  },

  async mounted() {
    this.loading = true;
    this.fetchCurrencies().catch(error => {
      this.errorMessage = ErrorManager.extractApiError(error);
    });
    this.searchQuotes({
      page: this.page,
      search: this.search
    });
  },

  watch: {
    search() {
      this.debounceFetchQuotes({
        page: 1,
        search: this.search
      });
    },

    page: {
      handler(page: number) {
        this.debounceFetchQuotes({
          page: page,
          search: this.search
        });
      }
    }
  },

  methods: {
    timeAgo,
    moment,

    ...mapActions({
      fetchQuote: 'quote/fetchQuote',
      fetchQuotes: 'quote/fetchQuotes',
      fetchCurrencies: 'currency/fetchCurrencies',
      exportQuoteRevisionItems: 'quote/exportQuoteItems',
      exportQuoteRevisionItemsMargins: 'quote/exportQuoteItemsMargins'
    }),

    clearAllErrors(): void {
      this.errorMessage = '';
    },

    debounceFetchQuotes(params: IQuoteFetchActionPayload) {
      this.clearAllErrors();
      this.loading = true;

      return this.debounce(this.searchQuotes, 200)(params);
    },

    searchQuotes(params: IQuoteFetchActionPayload) {
      /* this will update the URL query based on param values. */
      this.$router
        .replace({
          name: 'quote-list',
          query: params as Record<string, any>
        })
        .catch(e => e); //catch navigation duplication error

      return this.fetchQuotes(params)
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    goToQuote(quote: Quote) {
      this.$router.push({
        name: 'quote-revision-detail',
        params: {
          quoteId: String(quote.number),
          quoteRevision: String(quote.revision)
        }
      });
    },

    exportQuoteItems(quote: Quote) {
      this.exportQuoteRevisionItems({
        quoteId: quote.number,
        revisionId: quote.revision
      })

        .then((data: Blob) => {
          this.createQuoteItemXlsx(data, quote);
        })
        .catch(error => {
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Error: ${ErrorManager.extractApiError(error)}`,
            btnText: 'Close',
            timeout: 6000,
            action: function() {}
          });
        });
    },

    exportQuoteItemsMargins(quote: Quote) {
      this.exportQuoteRevisionItemsMargins({
        quoteId: quote.number,
        revisionId: quote.revision
      })

        .then((data: Blob) => {
          this.createQuoteItemXlsx(data, quote);
        })
        .catch(error => {
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Error: ${ErrorManager.extractApiError(error)}`,
            btnText: 'Close',
            timeout: 6000,
            action: function() {}
          });
        });
    },

    openRevision(quote: Quote) {
      const revisionDialog = this.$refs.quoteRevisionDialog as InstanceType<
        typeof QuotesRevisionHistoryDialog
      >;

      revisionDialog.open(quote);
    },

    async getActivities(quote: Quote) {
      const dialog = this.$refs.quoteActivityDialog as InstanceType<
        typeof QuoteActivityDialog
      >;
      dialog.open(quote);
    },

    createQuoteItemXlsx(data: Blob, quote: IQuote) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      const linkName = `quote-${quote.number}-${quote.revision}-${moment(
        new Date()
      ).format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
      link.href = url;

      link.setAttribute('download', linkName); //or any other extension
      document.body.appendChild(link);
      link.click();
    }
  }
});
